import { useState, useEffect, useCallback } from 'react';
import GameEngineAPI, { GameState, Move } from '../services/gameEngineApi';

export interface UseLudoGameProps {
  gameType: 'classic-ludo' | 'naija-ludo';
  gameId?: string | null; // Optional game ID to load directly
}

export const useLudoGame = ({
  gameType,
  gameId: directGameId,
}: UseLudoGameProps) => {
  // State
  const [gameId, setGameId] = useState<string | null>(directGameId || null);
  const [backendGameState, setBackendGameState] = useState<any>(null);
  const [availableMoves, setAvailableMoves] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRolling, setIsRolling] = useState(false);
  const [celebratingPiece, setCelebratingPiece] = useState<any>(null);

  // Store the complete dice result for Naija Ludo (includes dice1, dice2, total)
  const [lastDiceResult, setLastDiceResult] = useState<any>(null);

  // Initialize game on component mount
  const initializeGame = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // If we have a direct game ID (from AI game creation), use it
      if (directGameId) {
        setGameId(directGameId);

        // Get the specific game state
        const gameStateResponse =
          await GameEngineAPI.getGameState(directGameId);

        if (gameStateResponse.success && gameStateResponse.gameState) {
          setBackendGameState(gameStateResponse.gameState);
          console.log('Loaded direct game:', directGameId);
        } else {
          throw new Error(gameStateResponse.error || 'Failed to load game');
        }
      } else {
        // Check if player already has an active game
        const playerGameResponse = await GameEngineAPI.getPlayerGame();

        if (
          playerGameResponse.success &&
          playerGameResponse.gameId &&
          playerGameResponse.gameState
        ) {
          // Player has an existing game
          setGameId(playerGameResponse.gameId);
          setBackendGameState(playerGameResponse.gameState);
          console.log('Resumed existing game:', playerGameResponse.gameId);
        } else {
          // Create a new game
          const createResponse = await GameEngineAPI.createGame(
            gameType,
            4,
            false
          );

          if (
            createResponse.success &&
            createResponse.gameId &&
            createResponse.gameState
          ) {
            setGameId(createResponse.gameId);
            setBackendGameState(createResponse.gameState);
            console.log('Created new game:', createResponse.gameId);
          } else {
            throw new Error(createResponse.error || 'Failed to create game');
          }
        }
      }
    } catch (err) {
      console.error('Failed to initialize game:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to initialize game'
      );
    } finally {
      setIsLoading(false);
    }
  }, [gameType, directGameId]);

  // Start game
  const startGame = useCallback(async () => {
    if (!gameId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await GameEngineAPI.startGame(gameId);

      if (response.success && response.gameState) {
        setBackendGameState(response.gameState);
        console.log('Game started successfully');
      } else {
        throw new Error(response.error || 'Failed to start game');
      }
    } catch (err) {
      console.error('Failed to start game:', err);
      setError(err instanceof Error ? err.message : 'Failed to start game');
    } finally {
      setIsLoading(false);
    }
  }, [gameId]);

  // Roll dice
  const rollDice = useCallback(async () => {
    if (!gameId || !backendGameState || isRolling) return;

    setIsRolling(true);
    setError(null);

    try {
      const response = await GameEngineAPI.rollDice(gameId);

      if (response.success && response.gameState) {
        setBackendGameState(response.gameState);

        // Get available moves after dice roll
        if (response.availableMoves) {
          setAvailableMoves(response.availableMoves);
        }

        // Log dice result correctly - backend returns diceResult.value
        const diceValue = response.diceResult?.value;
        console.log('Dice rolled:', diceValue);

        // Store the complete dice result for Naija Ludo (includes dice1, dice2, total)
        setLastDiceResult(response.diceResult);
      } else {
        throw new Error(response.error || 'Failed to roll dice');
      }
    } catch (err) {
      console.error('Failed to roll dice:', err);
      setError(err instanceof Error ? err.message : 'Failed to roll dice');
    } finally {
      setIsRolling(false);
    }
  }, [gameId, backendGameState, isRolling]);

  // Move piece
  const movePiece = useCallback(
    async (playerIndex: number, pieceIndex: number, diceValue: number) => {
      if (!gameId || !backendGameState) return;

      setIsLoading(true);
      setError(null);

      try {
        const currentPlayer = backendGameState.players[playerIndex];
        const piece = currentPlayer.pieces[pieceIndex];

        if (!piece) {
          throw new Error('Invalid piece selection');
        }

        // Get current position and calculate target position
        const fromPosition = piece.position;
        const toPosition = fromPosition + diceValue; // Simple calculation for now

        const response = await GameEngineAPI.movePiece(
          gameId,
          piece.id,
          diceValue,
          fromPosition,
          toPosition
        );

        if (response.success && response.gameState) {
          setBackendGameState(response.gameState);
          setAvailableMoves([]); // Clear available moves after move

          // Check if piece reached home for celebration
          const updatedPlayer = response.gameState.players[playerIndex];
          const updatedPiece = updatedPlayer.pieces[pieceIndex];

          if (updatedPiece.position >= 100) {
            // Assuming 100+ is home
            setCelebratingPiece({
              playerId: currentPlayer.id,
              pieceId: piece.id,
              color: currentPlayer.color,
            });

            // Clear celebration after 2 seconds
            setTimeout(() => {
              setCelebratingPiece(null);
            }, 2000);
          }

          console.log('Piece moved successfully');
        } else {
          throw new Error(response.error || 'Failed to move piece');
        }
      } catch (err) {
        console.error('Failed to move piece:', err);
        setError(err instanceof Error ? err.message : 'Failed to move piece');
      } finally {
        setIsLoading(false);
      }
    },
    [gameId, backendGameState]
  );

  // Refresh game state
  const refreshGameState = useCallback(async () => {
    if (!gameId) return;

    try {
      const response = await GameEngineAPI.getGameState(gameId);

      if (response.success && response.gameState) {
        setBackendGameState(response.gameState);
      } else {
        throw new Error(response.error || 'Failed to refresh game state');
      }
    } catch (err) {
      console.error('Failed to refresh game state:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to refresh game state'
      );
    }
  }, [gameId]);

  // Initialize game on mount
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  return {
    // State
    gameId,
    backendGameState,
    availableMoves,
    isLoading,
    error,
    isRolling,
    celebratingPiece,
    lastDiceResult,

    // Actions
    initializeGame,
    startGame,
    rollDice,
    movePiece,
    refreshGameState,
    setError,
    setCelebratingPiece,
  };
};
